import { safely } from "./contracts";
import { API_NAMES } from "./registry";

/** Creator-local payload; the controller maps its result to the published SDK union. */
export interface EarlyRequestEntry {
  name: string;
  method: string;
  startEpochMs: number;
  startMono: number;
  /** Resource timing lookup only. Never a span attribute, log or persistent value. */
  url: string;
  result: number | "failure";
  endMono: number;
  retryCount: number;
}
export type EarlyRequestCapture = Readonly<{ kind: "early" }>;
type Pending = Omit<EarlyRequestEntry, "result" | "endMono"> &
  Partial<Pick<EarlyRequestEntry, "result" | "endMono">>;
type Recorder = (entry: Readonly<EarlyRequestEntry>) => boolean | void;
const TTL = 60000;
const LIMIT = 16;

/** No SDK imports, requests, headers or listeners. All work is synchronous and bounded. */
export function createEarlyRequestBuffer(deps: {
  /** Rechecked for every logical capture; existing tokens keep their call-time owner. */
  mayCapture(): boolean;
  clock: { now(): number; epochNow(): number };
  schedule(ms: number, work: () => void): () => void;
}) {
  let phase: "collecting" | "recording" | "discarded" = "collecting";
  let captures = new WeakMap<
    EarlyRequestCapture,
    { name: string; attempts: number }
  >();
  const entries = new Map<number, Pending>();
  let sequence = 0;
  let recorder: Recorder | undefined;
  let cancelTimer: (() => void) | undefined;
  const opened = safely(() => deps.clock.now(), NaN);
  const deadline = opened + TTL;

  function discard() {
    phase = "discarded";
    entries.clear();
    captures = new WeakMap();
    recorder = undefined;
    safely(() => cancelTimer?.(), undefined);
    cancelTimer = undefined;
  }
  function currentTime() {
    const now = safely(() => deps.clock.now(), NaN);
    if (
      !Number.isFinite(now) ||
      now < opened ||
      (phase === "collecting" && now >= deadline)
    ) {
      discard();
      return;
    }
    if (phase === "discarded") return;
    entries.forEach((entry, id) => {
      if (now - entry.startMono >= TTL) entries.delete(id);
    });
    return now;
  }
  function arm() {
    safely(() => cancelTimer?.(), undefined);
    cancelTimer = undefined;
    if (phase === "discarded") return;
    let next = phase === "collecting" ? deadline : Infinity;
    entries.forEach((entry) => {
      next = Math.min(next, entry.startMono + TTL);
    });
    if (!Number.isFinite(next)) return;
    try {
      const now = currentTime();
      if (now === undefined) return;
      cancelTimer = deps.schedule(Math.max(0, next - now), () => {
        if (currentTime() !== undefined) arm();
      });
    } catch {
      discard(); // No scheduler means no enforceable retention bound.
    }
  }
  function deliver(id: number) {
    const entry = entries.get(id);
    if (
      phase !== "recording" ||
      !recorder ||
      entry?.endMono === undefined ||
      entry.result === undefined
    )
      return;
    entries.delete(id); // Remove before calling external code: reentrant flush cannot duplicate it.
    safely(
      () => recorder?.(Object.freeze({ ...entry } as EarlyRequestEntry)),
      undefined
    );
  }
  if (!Number.isFinite(opened) || opened < 0) discard();
  else arm();

  return {
    captureRequest(name: string): EarlyRequestCapture | undefined {
      return safely(() => {
        if (
          deps.mayCapture() !== true ||
          currentTime() === undefined ||
          phase !== "collecting" ||
          !API_NAMES.some((registered) => registered === name)
        )
          return;
        const token = Object.freeze({ kind: "early" as const });
        captures.set(token, { name, attempts: 0 });
        return token;
      }, undefined);
    },
    startRequest(
      token: EarlyRequestCapture,
      input: { method: string; url: string }
    ) {
      return safely(() => {
        const now = currentTime();
        const captured = captures.get(token);
        if (now === undefined || now >= deadline || !captured) return;
        const epoch = deps.clock.epochNow();
        if (
          !Number.isFinite(epoch) ||
          epoch <= 0 ||
          typeof input.url !== "string"
        )
          return;
        const method = input.method.toUpperCase();
        const id = sequence++;
        const entry: Pending = {
          name: captured.name,
          method: [
            "GET",
            "POST",
            "PUT",
            "PATCH",
            "DELETE",
            "HEAD",
            "OPTIONS",
          ].includes(method)
            ? method
            : "_OTHER",
          startEpochMs: epoch,
          startMono: now,
          url: input.url,
          retryCount: Math.min(5, captured.attempts++),
        };
        if (entries.size === LIMIT)
          entries.delete(entries.keys().next().value!);
        entries.set(id, entry);
        arm();
        return {
          finish(status?: number) {
            safely(() => {
              const end = currentTime();
              const pending = entries.get(id);
              if (
                end === undefined ||
                !pending ||
                pending.endMono !== undefined
              )
                return;
              if (end < pending.startMono) {
                entries.delete(id);
                arm();
                return;
              }
              pending.endMono = end;
              pending.result =
                Number.isInteger(status) && status! >= 100 && status! <= 599
                  ? status
                  : "failure";
              deliver(id);
              arm();
            }, undefined);
          },
        };
      }, undefined);
    },
    // Ownership transfer inside SDK configure; flush later handles only remaining/in-flight entries.
    takeCompleted(): readonly EarlyRequestEntry[] {
      return safely(() => {
        if (phase !== "collecting" || currentTime() === undefined) return [];
        const completed: EarlyRequestEntry[] = [];
        entries.forEach((entry, id) => {
          if (entry.endMono === undefined || entry.result === undefined) return;
          entries.delete(id);
          completed.push(Object.freeze({ ...entry } as EarlyRequestEntry));
        });
        arm();
        return completed;
      }, []);
    },
    // Call only after SDK configuration permits export, never on pending activation.
    flush(recordEarlyRequest: Recorder) {
      safely(() => {
        if (
          currentTime() === undefined ||
          phase !== "collecting" ||
          typeof recordEarlyRequest !== "function"
        )
          return;
        phase = "recording";
        recorder = recordEarlyRequest;
        Array.from(entries.keys()).forEach(deliver);
        arm();
      }, undefined);
    },
    // OFF/kill is terminal for this document's early window; late completions cannot revive it.
    discard,
  };
}
